"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.timeFromValue = exports.dateFromValue = exports.coerceTime = exports.serialTime = exports.coerceDate = exports.serialDate = void 0;
var value_1 = require("../evaluator/value");
var type_1 = require("../types/type");
var moment_timezone_1 = __importDefault(require("moment-timezone"));
var util_1 = require("../evaluator/util");
var evaluationErrors_1 = require("../evaluator/evaluationErrors");
var dateValues_1 = require("../evaluator/dateValues");
var __1 = require("..");
/**
 * Converts a date to an Excel serial date, which is defined to be the number of days since Dec 31, 1899.
 * For example, Jan 1, 1900 is serial number 1 and Jan 1, 2008 is serial number 39448.
 */
function serialDate(moment) {
    // These magic dates are used for the following reasons:
    // 1. Serial dates are 1-indexed, not 0-indexed. So, starting from Dec 31, 1899 makes Jan 1, 1900 serial number 1.
    // 2. Excel intentionally recreates a bug in Lotus 1-2-3. Specifically, Lotus incorrectly considered 1900 to be a
    //    leap year, meaning serial dates after Mar 1, 1900 are 1 too high. So, when converting dates after then to
    //    serial dates, start from Dec 30, 1899, so that the result is 1 higher than the actual definition.
    // See: https://stackoverflow.com/a/10560281/4731558
    if (moment.isBefore(new Date(1900, 0, 1)) || moment.isAfter(new Date(9999, 11, 31))) {
        throw new Error("Date is out of bounds for serial date conversion.");
    }
    var start;
    // JS uses zero-indexed months
    if (moment.isSameOrBefore(new Date(1900, 2, 1))) {
        start = new Date(1899, 11, 31);
    }
    else {
        start = new Date(1899, 11, 30);
    }
    return moment.diff(start, "days");
}
exports.serialDate = serialDate;
/**
 * Creates a Moment object from a given input.
 * If the input is a Date, it's passed through.
 * If the input is a String, it's parsed with Moment.
 * If the input is a number, it's converted from an excel serial date.
 */
function coerceDate(date, opts) {
    if (opts === void 0) { opts = {}; }
    if (typeof date === "number") {
        // Dates after serial date 60 (Mar 1, 1900) are 1 too high, so decrement them.
        // See the comment in `serialDate` for a more detailed explanation.
        if (date > 60)
            date -= 1;
        return moment_timezone_1.default(new Date(1899, 11, 31)).add(date, "days");
    }
    else if (date === "") {
        throw new __1.FormulaError("Cannot parse date from empty string");
    }
    else {
        if (opts.timeZone)
            return moment_timezone_1.default.tz(date, opts.timeZone);
        return moment_timezone_1.default(date);
    }
}
exports.coerceDate = coerceDate;
function serialTime(time) {
    return (time.hours() / 24.0) + (time.minutes() / (24.0 * 60)) + (time.seconds() / (24.0 * 60 * 60));
}
exports.serialTime = serialTime;
function coerceTime(time) {
    if (typeof time === 'number') {
        var hours = Math.floor(24 * time);
        var minutes = Math.floor(24 * 60 * time) % 60;
        var seconds = Math.floor(24 * 60 * 60 * time) % 60;
        return moment_timezone_1.default().hours(hours).minutes(minutes).seconds(seconds);
    }
    else if (typeof time === 'string') {
        if (time === "") {
            throw new __1.FormulaError("Cannot parse time from empty string");
        }
        return moment_timezone_1.default(time, ['hh:mm:ss a', 'H:mm:ss', 'hh:mm a', 'H:mm', 'hh a', 'H', moment_timezone_1.default.ISO_8601]);
    }
    else {
        return moment_timezone_1.default(time);
    }
}
exports.coerceTime = coerceTime;
function dateFromValue(value, opts) {
    if (opts === void 0) { opts = {}; }
    if (value instanceof dateValues_1.DateValue || value instanceof dateValues_1.DateTimeValue) {
        var newValue = value.moment.clone();
        if (opts.timeZone)
            newValue = newValue.tz(opts.timeZone);
        return newValue;
    }
    else if (util_1.isNumberValue(value) || util_1.isStringValue(value)) {
        return coerceDate(value.value, opts);
    }
    else {
        throw new evaluationErrors_1.NotApplicableError();
    }
}
exports.dateFromValue = dateFromValue;
function timeFromValue(value) {
    if (value instanceof dateValues_1.TimeValue || value instanceof dateValues_1.DateTimeValue) {
        return value.moment.clone();
    }
    else if (util_1.isNumberValue(value) || util_1.isStringValue(value)) {
        return coerceTime(value.value);
    }
    else {
        throw new evaluationErrors_1.NotApplicableError();
    }
}
exports.timeFromValue = timeFromValue;
var formatDateInvoke = function (_a) {
    var value = _a[0], format = _a[1], timeZone = _a[2];
    var timeZoneString = timeZone ? timeZone.value : "America/New_York";
    var formatString = format.value;
    var formattedValue = dateFromValue(value, { timeZone: timeZoneString }).format(formatString);
    return value_1.Value.string(formattedValue);
};
function registerDateFunctions(context) {
    var stringOrNumber = { type: "union", options: [type_1.StringType, type_1.NumberType] };
    context.registerFunction("date", {
        invokeWithASTParameters: false,
        signature: {
            type: "fixed",
            parameters: [stringOrNumber, stringOrNumber, stringOrNumber],
            returns: type_1.NumberType,
        },
        invoke: function (_a) {
            var yearValue = _a[0], monthValue = _a[1], dayValue = _a[2];
            var year = util_1.coerceNumber(yearValue);
            var month = util_1.coerceNumber(monthValue);
            var day = util_1.coerceNumber(dayValue);
            if (year >= 0 && year < 1900)
                year += 1900;
            else if (year < 0 || year > 10000)
                throw new evaluationErrors_1.NumericError();
            var date = moment_timezone_1.default({ year: year }).add(month - 1, "months").add(day - 1, "days");
            return new dateValues_1.DateValue(date);
        },
    });
    context.registerFunction("datevalue", {
        invokeWithASTParameters: false,
        signature: {
            type: "fixed",
            parameters: [type_1.StringType],
            returns: type_1.NumberType,
        },
        invoke: function (_a) {
            var str = _a[0];
            return new dateValues_1.DateValue(coerceDate(str.value));
        },
    });
    context.registerFunction("day", {
        invokeWithASTParameters: false,
        signature: {
            type: "fixed",
            parameters: [stringOrNumber],
            returns: type_1.NumberType,
        },
        invoke: function (_a) {
            var date = _a[0];
            return value_1.Value.number(dateFromValue(date).date());
        },
    });
    context.registerFunction("month", {
        invokeWithASTParameters: false,
        signature: {
            type: "fixed",
            parameters: [stringOrNumber],
            returns: type_1.NumberType,
        },
        invoke: function (_a) {
            var date = _a[0];
            // JS months are zero-indexed
            return value_1.Value.number(dateFromValue(date).month() + 1);
        }
    });
    context.registerFunction("year", {
        invokeWithASTParameters: false,
        signature: {
            type: "fixed",
            parameters: [stringOrNumber],
            returns: type_1.NumberType,
        },
        invoke: function (_a) {
            var date = _a[0];
            return value_1.Value.number(dateFromValue(date).year());
        }
    });
    context.registerFunction("days", {
        invokeWithASTParameters: false,
        signature: {
            type: "fixed",
            parameters: [stringOrNumber, stringOrNumber],
            returns: type_1.NumberType,
        },
        invoke: function (_a) {
            var endValue = _a[0], startValue = _a[1];
            var end = dateFromValue(endValue);
            var start = dateFromValue(startValue);
            return value_1.Value.number(end.diff(start, "days"));
        },
    });
    context.registerFunction("today", {
        invokeWithASTParameters: false,
        signature: {
            type: "fixed",
            parameters: [],
            returns: type_1.NumberType,
        },
        invoke: function (_a) {
            return new dateValues_1.DateValue(moment_timezone_1.default().startOf('day'));
        }
    });
    context.registerFunction("edate", {
        invokeWithASTParameters: false,
        signature: {
            type: "fixed",
            parameters: [stringOrNumber, stringOrNumber],
            returns: type_1.NumberType,
        },
        invoke: function (_a) {
            var start = _a[0], months = _a[1];
            var startDate = dateFromValue(start);
            var number = util_1.coerceNumber(months);
            return new dateValues_1.DateValue(startDate.add(number, "months"));
        },
    });
    context.registerFunction('eomonth', {
        invokeWithASTParameters: false,
        signature: {
            type: "fixed",
            parameters: [stringOrNumber, stringOrNumber],
            returns: type_1.NumberType,
        },
        invoke: function (_a) {
            var start = _a[0], months = _a[1];
            var startDate = dateFromValue(start);
            var number = util_1.coerceNumber(months);
            return new dateValues_1.DateValue(startDate.add(number, 'months').endOf('month').startOf('day'));
        },
    });
    var invokeWeekday = function (date, returnType) {
        if (returnType === 1) {
            return [2, 3, 4, 5, 6, 7][date.isoWeekday() - 1];
        }
        else if (returnType === 2) {
            return date.isoWeekday();
        }
        else if (returnType === 3) {
            return date.isoWeekday() - 1;
        }
        else {
            // Map ISO weekday numbers to Excel format. See https://support.microsoft.com/en-us/office/weekday-function-60e44483-2ed1-439f-8bd0-e404c190949a
            return [
                [1, 2, 3, 4, 5, 6, 7],
                [7, 1, 2, 3, 4, 5, 6],
                [6, 7, 1, 2, 3, 4, 5],
                [5, 6, 7, 1, 2, 3, 4],
                [4, 5, 6, 7, 1, 2, 3],
                [3, 4, 5, 6, 7, 1, 2],
                [2, 3, 4, 5, 6, 7, 1]
            ][returnType - 11][date.isoWeekday() - 1];
        }
    };
    context.registerFunction('weekday', [
        {
            invokeWithASTParameters: false,
            signature: {
                type: "fixed",
                parameters: [stringOrNumber, type_1.NumberType],
                returns: type_1.NumberType,
            },
            invoke: function (_a) {
                var date = _a[0], returnType = _a[1];
                return value_1.Value.number(invokeWeekday(dateFromValue(date), util_1.coerceNumber(returnType)));
            },
        },
        {
            invokeWithASTParameters: false,
            signature: {
                type: "fixed",
                parameters: [stringOrNumber],
                returns: type_1.NumberType,
            },
            invoke: function (_a) {
                var date = _a[0];
                return value_1.Value.number(invokeWeekday(dateFromValue(date), 1));
            },
        },
    ]);
    var invokeWeeknum = function (date, returnType) {
        // See https://support.microsoft.com/en-us/office/weeknum-function-e5c43a03-b4ab-426c-b411-b18c13c75340
        // for definitions of the different return types.
        // System 1 defines the week containing Jan 1 to be week 1.
        // System 2 is ISO 8601 week years and is only used for return type 21.
        if (returnType === 21) {
            return date.isoWeek();
        }
        else if (returnType === 1 || returnType === 2 || (returnType >= 11 && returnType <= 17)) {
            var weekStartDay = void 0;
            switch (returnType) {
                case 1:
                    weekStartDay = 7;
                    break;
                case 2:
                    weekStartDay = 1;
                    break;
                default:
                    // 11 through 17 -> 1 (Mon) through 7 (Sun)
                    weekStartDay = returnType - 10;
                    break;
            }
            var yearStart = moment_timezone_1.default({ year: date.year(), month: 0, day: 1 });
            // The first week of a year is the week which contains Jan 1. So, depending onthe week start day, the first
            // week of the year may begin in a previous year.
            // Call .clone to prevent altering yearStart
            var yearFirstWeekStart = yearStart.clone().subtract(yearStart.isoWeekday() - weekStartDay, "days");
            var days = date.diff(yearFirstWeekStart, "days");
            // Note: Math.ceil does not work here because x.floor + 1 != x.ceil for any integer x.
            // We always want to add 1, because, for example, if there are 14 days between new year's day and the given date,
            // while 2 weeks have _passed_, that means we're right at the beginning of the third week.
            var weeks = Math.floor(days / 7) + 1;
            // If the beginning of the year is on a day earlier than the defined beginning of the week,
            // add 1 to the weeks (representing the week in which Jan 1 occurs).
            if (yearStart.isoWeekday() < weekStartDay)
                weeks += 1;
            return weeks;
        }
        else {
            throw new evaluationErrors_1.NotApplicableError();
        }
    };
    context.registerFunction('weeknum', [
        {
            invokeWithASTParameters: false,
            signature: {
                type: "fixed",
                parameters: [stringOrNumber, type_1.NumberType],
                returns: type_1.NumberType,
            },
            invoke: function (_a) {
                var date = _a[0], returnType = _a[1];
                return value_1.Value.number(invokeWeeknum(dateFromValue(date), util_1.coerceNumber(returnType)));
            },
        },
        {
            invokeWithASTParameters: false,
            signature: {
                type: "fixed",
                parameters: [stringOrNumber],
                returns: type_1.NumberType,
            },
            invoke: function (_a) {
                var date = _a[0];
                return value_1.Value.number(invokeWeeknum(dateFromValue(date), 1));
            },
        },
    ]);
    var invokeWorkday = function (date, days, holidays) {
        var daysRemaining = Math.floor(days);
        while (daysRemaining > 0) {
            date = date.add(1, 'day');
            if (date.isoWeekday() !== 6 && date.isoWeekday() !== 7 && !holidays.some(function (h) { return h.isSame(date, 'day'); })) {
                daysRemaining -= 1;
            }
        }
        return date;
    };
    context.registerFunction('workday', [
        {
            invokeWithASTParameters: false,
            signature: {
                type: "fixed",
                parameters: [stringOrNumber, type_1.NumberType, { type: "array", elements: stringOrNumber }],
                returns: type_1.NumberType,
            },
            invoke: function (_a) {
                var startDate = _a[0], days = _a[1], holidays = _a[2];
                var holidayMoments = holidays.elements.map(function (el) { return dateFromValue(el); });
                return new dateValues_1.DateValue(invokeWorkday(dateFromValue(startDate), util_1.coerceNumber(days), holidayMoments));
            },
        },
        {
            invokeWithASTParameters: false,
            signature: {
                type: "fixed",
                parameters: [stringOrNumber, type_1.NumberType],
                returns: type_1.NumberType,
            },
            invoke: function (_a) {
                var startDate = _a[0], days = _a[1];
                return new dateValues_1.DateValue(invokeWorkday(dateFromValue(startDate), util_1.coerceNumber(days), []));
            },
        },
    ]);
    context.registerFunction('time', {
        invokeWithASTParameters: false,
        signature: {
            type: "fixed",
            parameters: [stringOrNumber, stringOrNumber, stringOrNumber],
            returns: type_1.NumberType,
        },
        invoke: function (_a) {
            var hour = _a[0], minute = _a[1], second = _a[2];
            var hourNum = util_1.coerceNumber(hour);
            if (hourNum < 0) {
                throw new evaluationErrors_1.NumericError();
            }
            // add units minutes/seconds instead of setting at initialization so that negative values work
            var m = moment_timezone_1.default({ hour: hourNum })
                .add(util_1.coerceNumber(minute), 'minutes')
                .add(util_1.coerceNumber(second), 'seconds');
            return new dateValues_1.TimeValue(m);
        },
    });
    context.registerFunction('timevalue', {
        invokeWithASTParameters: false,
        signature: {
            type: "fixed",
            parameters: [type_1.StringType],
            returns: type_1.NumberType,
        },
        invoke: function (_a) {
            var str = _a[0];
            return new dateValues_1.TimeValue(coerceTime(str.value));
        },
    });
    context.registerFunction("hour", {
        invokeWithASTParameters: false,
        signature: {
            type: "fixed",
            parameters: [stringOrNumber],
            returns: type_1.NumberType,
        },
        invoke: function (_a) {
            var val = _a[0];
            var time = timeFromValue(val);
            return value_1.Value.number(time.hour());
        }
    });
    context.registerFunction("minute", {
        invokeWithASTParameters: false,
        signature: {
            type: "fixed",
            parameters: [stringOrNumber],
            returns: type_1.NumberType,
        },
        invoke: function (_a) {
            var val = _a[0];
            var time = timeFromValue(val);
            return value_1.Value.number(time.minute());
        }
    });
    context.registerFunction("second", {
        invokeWithASTParameters: false,
        signature: {
            type: "fixed",
            parameters: [stringOrNumber],
            returns: type_1.NumberType,
        },
        invoke: function (_a) {
            var val = _a[0];
            var time = timeFromValue(val);
            return value_1.Value.number(time.second());
        }
    });
    context.registerFunction("now", {
        invokeWithASTParameters: false,
        signature: {
            type: "fixed",
            parameters: [],
            returns: type_1.NumberType,
        },
        invoke: function (_a) {
            return new dateValues_1.DateTimeValue(moment_timezone_1.default());
        },
    });
    context.registerFunction("formatdate", [
        {
            invokeWithASTParameters: false,
            signature: {
                type: "fixed",
                parameters: [stringOrNumber, type_1.StringType, type_1.StringType],
                returns: type_1.StringType,
            },
            invoke: formatDateInvoke,
        },
        {
            invokeWithASTParameters: false,
            signature: {
                type: "fixed",
                parameters: [stringOrNumber, type_1.StringType],
                returns: type_1.StringType,
            },
            invoke: formatDateInvoke,
        },
    ]);
}
exports.default = registerDateFunctions;
//# sourceMappingURL=dates.js.map